import { useCallback, useEffect, useState } from "react";
import { format, formatDistanceToNow } from "date-fns";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import type { Deal } from "../../lib/types";
import DealLink from "../DealLink";
import { daysInStage, daysInPhase, daysSinceCreated } from "../../lib/dealAge";
import { formatPhone } from "../../lib/phoneFormat";
import { invokeErrorMessage } from "../../lib/edgeError";
import { mapsHref, isAddressLinkable } from "../../lib/mapsHref";

/**
 * Joy's Office View — Climb Authorizations + Insufficient Damage tracking.
 *
 * Climb Auth: deals at `needs_climb_inspection`
 *   Joy authorizes → Robert/RS performs physical inspection
 *
 * Insufficient Damage: deals at `insufficient_damage`
 *   Claude handles text outreach. Joy monitors status.
 *
 * Both tabs show Twilio call/text buttons + last call/text on each card.
 */

type OfficeRow = Pick<
  Deal,
  | "id"
  | "homeowner_name"
  | "homeowner_phone"
  | "property_street"
  | "property_city"
  | "property_state"
  | "stage"
  | "days_in_stage"
  | "stage_entered_at"
  | "phase_entered_at"
  | "owner_id"
  | "drone_unable_to_fly"
  | "drone_unable_reason"
  | "needs_climb"
  | "drone_scheduled_date"
  | "claim_number"
  | "created_at"
>;

/** Last comms per deal — one text + one call */
interface LastComms {
  lastTextAt: string | null;
  lastTextBody: string | null;
  lastTextDirection: string | null;
  lastCallAt: string | null;
  lastCallDuration: number | null;
  lastCallDirection: string | null;
}

const COLS =
  "id,homeowner_name,homeowner_phone,property_street,property_city,property_state,stage,days_in_stage,stage_entered_at,phase_entered_at,owner_id,drone_unable_to_fly,drone_unable_reason,needs_climb,drone_scheduled_date,claim_number,created_at";

type ViewTab = "climb" | "insufficient";

export default function JoyOfficeView() {
  const { member } = useAuth();
  const [tab, setTab] = useState<ViewTab>("climb");
  const [climbDeals, setClimbDeals] = useState<OfficeRow[] | null>(null);
  const [insuffDeals, setInsuffDeals] = useState<OfficeRow[] | null>(null);
  const [members, setMembers] = useState<Record<string, string>>({});
  const [commsMap, setCommsMap] = useState<Record<string, LastComms>>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [textingDeal, setTextingDeal] = useState<string | null>(null);
  const [textBody, setTextBody] = useState("");
  const [sendingText, setSendingText] = useState(false);
  const [callingDeal, setCallingDeal] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const [
        { data: climb, error: e1 },
        { data: insuff, error: e2 },
        { data: mems },
      ] = await Promise.all([
        supabase
          .from("deals")
          .select(COLS)
          .eq("stage", "needs_climb_inspection")
          .order("created_at", { ascending: true }),
        supabase
          .from("deals")
          .select(COLS)
          .eq("stage", "insufficient_damage")
          .gte("drone_scheduled_date", "2026-04-01")
          .order("created_at", { ascending: false }),
        supabase
          .from("team_roster")
          .select("id,full_name")
          .eq("is_active", true),
      ]);
      if (e1 ?? e2) {
        if (!cancelled) setErr((e1 ?? e2)!.message);
        return;
      }
      if (!cancelled) {
        const climbRows = (climb ?? []) as unknown as OfficeRow[];
        const insuffRows = (insuff ?? []) as unknown as OfficeRow[];
        setClimbDeals(climbRows);
        setInsuffDeals(insuffRows);
        const map: Record<string, string> = {};
        (mems ?? []).forEach((m) => { map[m.id] = m.full_name; });
        setMembers(map);

        // Fetch last text + last call for all deal IDs
        const allIds = [...climbRows, ...insuffRows].map((d) => d.id);
        if (allIds.length > 0) {
          await loadComms(allIds, cancelled);
        }
      }
    }

    async function loadComms(dealIds: string[], wasCancelled: boolean) {
      // Last text per deal
      const { data: texts } = await supabase
        .from("twilio_messages")
        .select("deal_id,created_at,body,direction")
        .in("deal_id", dealIds)
        .order("created_at", { ascending: false });

      // Last call per deal
      const { data: calls } = await supabase
        .from("twilio_calls")
        .select("deal_id,created_at,duration_seconds,direction")
        .in("deal_id", dealIds)
        .order("created_at", { ascending: false });

      if (wasCancelled) return;

      const cMap: Record<string, LastComms> = {};
      // Initialize all
      dealIds.forEach((id) => {
        cMap[id] = {
          lastTextAt: null, lastTextBody: null, lastTextDirection: null,
          lastCallAt: null, lastCallDuration: null, lastCallDirection: null,
        };
      });
      // First occurrence per deal is the most recent (ordered desc)
      (texts ?? []).forEach((t: any) => {
        if (cMap[t.deal_id] && !cMap[t.deal_id].lastTextAt) {
          cMap[t.deal_id].lastTextAt = t.created_at;
          cMap[t.deal_id].lastTextBody = t.body;
          cMap[t.deal_id].lastTextDirection = t.direction;
        }
      });
      (calls ?? []).forEach((c: any) => {
        if (cMap[c.deal_id] && !cMap[c.deal_id].lastCallAt) {
          cMap[c.deal_id].lastCallAt = c.created_at;
          cMap[c.deal_id].lastCallDuration = c.duration_seconds;
          cMap[c.deal_id].lastCallDirection = c.direction;
        }
      });
      setCommsMap(cMap);
    }

    load();
    return () => { cancelled = true; };
  }, [reloadKey]);

  // ── Send a text to homeowner ──
  async function handleSendText(dealId: string, phone: string | null) {
    if (!phone || !textBody.trim() || sendingText) return;
    setSendingText(true);
    try {
      const { data, error } = await supabase.functions.invoke("twilio-send-sms", {
        body: { to: phone, body: textBody.trim(), deal_id: dealId, tag: "joy_office", sent_by: member?.id ?? null },
      });
      if (error) throw new Error(error.message || "Send failed");
      if (data?.error) throw new Error(data.error);

      // Update local comms state
      setCommsMap((prev) => ({
        ...prev,
        [dealId]: {
          ...prev[dealId],
          lastTextAt: new Date().toISOString(),
          lastTextBody: textBody.trim(),
          lastTextDirection: "outbound",
        },
      }));
      setTextBody("");
      setTextingDeal(null);
    } catch (e: any) {
      alert("Text failed: " + (e.message ?? e));
    } finally {
      setSendingText(false);
    }
  }

  // ── Start a call to homeowner ──
  async function handleCall(dealId: string, name: string | null, phone: string | null) {
    if (!phone) return;
    if (!window.confirm(`Call ${name ?? "homeowner"} at ${formatPhone(phone)}?\n\nYour phone will ring first, then we'll connect you.`)) return;
    setCallingDeal(dealId);
    try {
      const { data, error } = await supabase.functions.invoke("twilio-start-call", {
        body: { deal_id: dealId, rm_id: member?.id ?? null },
      });
      if (error) throw new Error(await invokeErrorMessage(error, "Call failed"));
      if (!data?.ok) throw new Error(data?.error || "Call failed");
      // Update local comms
      setCommsMap((prev) => ({
        ...prev,
        [dealId]: {
          ...prev[dealId],
          lastCallAt: new Date().toISOString(),
          lastCallDuration: null,
          lastCallDirection: "outbound",
        },
      }));
    } catch (e: any) {
      alert("Couldn't start call: " + (e.message ?? e));
    } finally {
      setCallingDeal(null);
    }
  }

  async function handleAuthorizeClimb(dealId: string) {
    if (!window.confirm("Authorize physical climb inspection for this property?")) return;
    setBusy((b) => ({ ...b, [dealId]: true }));
    try {
      const { error } = await supabase
        .from("deals")
        .update({
          needs_climb: true,
          // Move back to drone_scheduled so Robert/RS can be assigned
          stage: "drone_scheduled",
          days_in_stage: 0,
        })
        .eq("id", dealId);
      if (error) throw error;
      await supabase.from("activity_log").insert({
        deal_id: dealId,
        activity_type: "stage_change",
        title: "Climb Inspection Authorized",
        description: "Joy authorized physical climb inspection. Awaiting RS assignment.",
        metadata: { from: "needs_climb_inspection", to: "drone_scheduled", authorized_by: "joy" },
      });
      reload();
    } catch (e: any) {
      alert(`Authorization failed: ${e.message ?? e}`);
    } finally {
      setBusy((b) => ({ ...b, [dealId]: false }));
    }
  }

  async function handleDenyClimb(dealId: string) {
    if (!window.confirm("Deny climb inspection? Deal will move to Insufficient Damage.")) return;
    setBusy((b) => ({ ...b, [dealId]: true }));
    try {
      const { error } = await supabase
        .from("deals")
        .update({ stage: "insufficient_damage", days_in_stage: 0 })
        .eq("id", dealId);
      if (error) throw error;
      await supabase.from("activity_log").insert({
        deal_id: dealId,
        activity_type: "stage_change",
        title: "Climb Inspection Denied",
        description: "Joy denied climb inspection. Moved to Insufficient Damage.",
        metadata: { from: "needs_climb_inspection", to: "insufficient_damage" },
      });
      reload();
    } catch (e: any) {
      alert(`Deny failed: ${e.message ?? e}`);
    } finally {
      setBusy((b) => ({ ...b, [dealId]: false }));
    }
  }

  async function handleCloseWon(dealId: string, name: string | null) {
    if (!window.confirm(`Mark "${name ?? "this deal"}" as outreach complete? It will be removed from your queue.`)) return;
    setBusy((b) => ({ ...b, [dealId]: true }));
    try {
      const { error } = await supabase
        .from("deals")
        .update({ stage: "closed_lost", days_in_stage: 0 })
        .eq("id", dealId);
      if (error) throw error;
      await supabase.from("activity_log").insert({
        deal_id: dealId,
        activity_type: "stage_change",
        title: "Insufficient Damage — Outreach Complete",
        description: "Joy marked outreach complete. Report offered, review links sent. Moved to Closed Lost (insufficient damage).",
        metadata: { from: "insufficient_damage", to: "closed_lost", closed_by: "joy", reason: "insufficient_damage_outreach_complete" },
      });
      reload();
    } catch (e: any) {
      alert(`Close failed: ${e.message ?? e}`);
    } finally {
      setBusy((b) => ({ ...b, [dealId]: false }));
    }
  }

  if (err) return <div className="bg-red-50 text-red-700 p-4 rounded">{err}</div>;
  if (!climbDeals || !insuffDeals)
    return <div className="text-slate-500 py-8 text-center text-sm">Loading…</div>;

  const tabs: Array<{ key: ViewTab; label: string; count: number }> = [
    { key: "climb", label: "Climb Authorization", count: climbDeals.length },
    { key: "insufficient", label: "Insufficient Damage", count: insuffDeals.length },
  ];

  return (
    <div className="space-y-4">
      {/* Tab bar */}
      <div className="flex gap-2">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={
              "px-4 py-2 text-sm font-medium rounded-lg transition " +
              (tab === t.key
                ? "bg-bardin-ink text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200")
            }
          >
            {t.label}
            <span className="ml-1.5 text-xs opacity-70">({t.count})</span>
          </button>
        ))}
      </div>

      {/* ── CLIMB AUTHORIZATION ── */}
      {tab === "climb" && (
        <section>
          <p className="text-sm text-slate-500 mb-3">
            Drone couldn't fly — physical climb inspection needed. Authorize → Robert/RS performs.
          </p>

          {climbDeals.length === 0 ? (
            <div className="bg-white rounded-xl shadow p-6 text-sm text-slate-500 text-center">
              No properties awaiting climb authorization.
            </div>
          ) : (
            <div className="space-y-2">
              {climbDeals.map((d) => {
                const ownerName = d.owner_id ? members[d.owner_id] : null;
                const isBusy = !!busy[d.id];
                const comms = commsMap[d.id];

                return (
                  <div key={d.id} className="bg-white rounded-xl shadow px-4 py-3 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm text-bardin-ink">
                            <DealLink id={d.id}>{d.homeowner_name ?? "(no name)"}</DealLink>
                          </span>
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                            Needs Climb
                          </span>
                          {d.claim_number && (
                            <span className="text-[10px] text-slate-400">#{d.claim_number}</span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {isAddressLinkable(d) ? (
                            <a
                              href={mapsHref(d)}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="hover:underline hover:text-slate-700"
                            >
                              {[d.property_street, d.property_city, d.property_state].filter(Boolean).join(", ")}
                            </a>
                          ) : (
                            [d.property_street, d.property_city, d.property_state].filter(Boolean).join(", ") || "—"
                          )}
                        </div>
                        <div className="flex items-center gap-3 flex-wrap text-xs text-slate-500 mt-1">
                          {ownerName && <span>RM: {ownerName}</span>}
                          {d.drone_scheduled_date && (
                            <span>
                              Flagged: {format(new Date(d.drone_scheduled_date), "MMM d")}
                            </span>
                          )}
                          <span>{daysInStage(d)}d waiting · {daysInPhase(d) ?? "?"}d in phase · {daysSinceCreated(d) ?? "?"}d since created</span>
                          {d.homeowner_phone && (
                            <span className="text-slate-400">{formatPhone(d.homeowner_phone)}</span>
                          )}
                        </div>
                        {d.drone_unable_reason && (
                          <div className="text-xs text-slate-600 mt-1 italic bg-amber-50 px-2 py-1 rounded">
                            Reason: {d.drone_unable_reason}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* ── Last Comms Row ── */}
                    <CommsRow comms={comms} />

                    {/* ── Action buttons ── */}
                    <div className="flex items-center gap-2 pt-1 flex-wrap">
                      <button
                        onClick={() => handleAuthorizeClimb(d.id)}
                        disabled={isBusy}
                        className="px-4 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 transition disabled:opacity-50"
                      >
                        {isBusy ? "Authorizing…" : "Authorize Climb"}
                      </button>
                      <button
                        onClick={() => handleDenyClimb(d.id)}
                        disabled={isBusy}
                        className="px-4 py-1.5 rounded-lg bg-red-50 text-red-600 text-xs font-medium border border-red-200 hover:bg-red-100 transition disabled:opacity-50"
                      >
                        Deny → Insufficient
                      </button>

                      <div className="ml-auto flex items-center gap-1.5">
                        <button
                          onClick={() => handleCall(d.id, d.homeowner_name, d.homeowner_phone)}
                          disabled={!d.homeowner_phone || callingDeal === d.id}
                          className="px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-medium border border-emerald-200 hover:bg-emerald-100 transition disabled:opacity-40"
                          title={d.homeowner_phone ? `Call ${formatPhone(d.homeowner_phone)}` : "No phone"}
                        >
                          {callingDeal === d.id ? "Dialing…" : "📞 Call"}
                        </button>
                        <button
                          onClick={() => { setTextingDeal(textingDeal === d.id ? null : d.id); setTextBody(""); }}
                          disabled={!d.homeowner_phone}
                          className="px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 text-xs font-medium border border-blue-200 hover:bg-blue-100 transition disabled:opacity-40"
                          title={d.homeowner_phone ? `Text ${formatPhone(d.homeowner_phone)}` : "No phone"}
                        >
                          💬 Text
                        </button>
                      </div>
                    </div>

                    {/* ── Inline text composer ── */}
                    {textingDeal === d.id && d.homeowner_phone && (
                      <div className="flex gap-2 pt-1">
                        <input
                          type="text"
                          value={textBody}
                          onChange={(e) => setTextBody(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") handleSendText(d.id, d.homeowner_phone); }}
                          placeholder={`Text ${d.homeowner_name ?? "homeowner"}…`}
                          className="flex-1 text-xs border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-bardin-teal"
                          autoFocus
                        />
                        <button
                          onClick={() => handleSendText(d.id, d.homeowner_phone)}
                          disabled={!textBody.trim() || sendingText}
                          className="px-3 py-1.5 rounded-lg bg-bardin-teal text-white text-xs font-semibold hover:bg-bardin-teal/90 transition disabled:opacity-50"
                        >
                          {sendingText ? "Sending…" : "Send"}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* ── INSUFFICIENT DAMAGE (Claude handles text outreach) ── */}
      {tab === "insufficient" && (
        <section>
          <p className="text-sm text-slate-500 mb-3">
            Deals flagged as insufficient damage. Greg handles homeowner text outreach.
          </p>

          {insuffDeals.length === 0 ? (
            <div className="bg-white rounded-xl shadow p-6 text-sm text-slate-500 text-center">
              No deals flagged as insufficient damage.
            </div>
          ) : (
            <div className="space-y-2">
              {insuffDeals.map((d) => {
                const ownerName = d.owner_id ? members[d.owner_id] : null;
                const comms = commsMap[d.id];
                return (
                  <div key={d.id} className="bg-white rounded-xl shadow px-4 py-3 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm text-bardin-ink">
                            <DealLink id={d.id}>{d.homeowner_name ?? "(no name)"}</DealLink>
                          </span>
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                            Insufficient Damage
                          </span>
                          {d.claim_number && (
                            <span className="text-[10px] text-slate-400">#{d.claim_number}</span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {isAddressLinkable(d) ? (
                            <a
                              href={mapsHref(d)}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="hover:underline hover:text-slate-700"
                            >
                              {[d.property_street, d.property_city].filter(Boolean).join(", ")}
                            </a>
                          ) : (
                            [d.property_street, d.property_city].filter(Boolean).join(", ") || "—"
                          )}
                        </div>
                        <div className="flex items-center gap-3 flex-wrap text-xs text-slate-500 mt-1">
                          {ownerName && <span>RM: {ownerName}</span>}
                          <span>{daysInStage(d)}d in stage · {daysInPhase(d) ?? "?"}d in phase · {daysSinceCreated(d) ?? "?"}d since created</span>
                          {d.homeowner_phone && (
                            <span className="text-slate-400">{formatPhone(d.homeowner_phone)}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* ── Last Comms Row ── */}
                    <CommsRow comms={comms} />

                    {/* ── Call / Text / Close Won buttons ── */}
                    <div className="flex items-center gap-1.5 pt-1 flex-wrap">
                      <button
                        onClick={() => handleCloseWon(d.id, d.homeowner_name)}
                        disabled={!!busy[d.id]}
                        className="px-4 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 transition disabled:opacity-50"
                      >
                        {busy[d.id] ? "Closing…" : "✅ Close Won"}
                      </button>
                      <div className="ml-auto flex items-center gap-1.5">
                        <button
                          onClick={() => handleCall(d.id, d.homeowner_name, d.homeowner_phone)}
                          disabled={!d.homeowner_phone || callingDeal === d.id}
                          className="px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-medium border border-emerald-200 hover:bg-emerald-100 transition disabled:opacity-40"
                          title={d.homeowner_phone ? `Call ${formatPhone(d.homeowner_phone)}` : "No phone"}
                        >
                          {callingDeal === d.id ? "Dialing…" : "📞 Call"}
                        </button>
                        <button
                          onClick={() => { setTextingDeal(textingDeal === d.id ? null : d.id); setTextBody(""); }}
                          disabled={!d.homeowner_phone}
                          className="px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 text-xs font-medium border border-blue-200 hover:bg-blue-100 transition disabled:opacity-40"
                          title={d.homeowner_phone ? `Text ${formatPhone(d.homeowner_phone)}` : "No phone"}
                        >
                          💬 Text
                        </button>
                      </div>
                    </div>

                    {/* ── Inline text composer ── */}
                    {textingDeal === d.id && d.homeowner_phone && (
                      <div className="flex gap-2 pt-1">
                        <input
                          type="text"
                          value={textBody}
                          onChange={(e) => setTextBody(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") handleSendText(d.id, d.homeowner_phone); }}
                          placeholder={`Text ${d.homeowner_name ?? "homeowner"}…`}
                          className="flex-1 text-xs border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-bardin-teal"
                          autoFocus
                        />
                        <button
                          onClick={() => handleSendText(d.id, d.homeowner_phone)}
                          disabled={!textBody.trim() || sendingText}
                          className="px-3 py-1.5 rounded-lg bg-bardin-teal text-white text-xs font-semibold hover:bg-bardin-teal/90 transition disabled:opacity-50"
                        >
                          {sendingText ? "Sending…" : "Send"}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CommsRow — shows last text + last call on a deal card
// ─────────────────────────────────────────────────────────────────────────────

function CommsRow({ comms }: { comms: LastComms | undefined }) {
  if (!comms) return null;
  const hasText = !!comms.lastTextAt;
  const hasCall = !!comms.lastCallAt;
  if (!hasText && !hasCall) return null;

  const ago = (iso: string) => {
    try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
    catch { return ""; }
  };

  const truncate = (s: string, max: number) =>
    s.length <= max ? s : s.slice(0, max) + "…";

  return (
    <div className="flex items-center gap-4 text-[11px] text-slate-500 bg-slate-50 rounded-lg px-3 py-1.5">
      {hasText && (
        <span className="flex items-center gap-1 min-w-0">
          <span>{comms.lastTextDirection === "inbound" ? "📥" : "📤"}</span>
          <span className="font-medium text-slate-600">Last text</span>
          <span className="text-slate-400">{ago(comms.lastTextAt!)}</span>
          {comms.lastTextBody && (
            <span className="text-slate-400 truncate max-w-[180px]" title={comms.lastTextBody}>
              — {truncate(comms.lastTextBody, 40)}
            </span>
          )}
        </span>
      )}
      {hasCall && (
        <span className="flex items-center gap-1">
          <span>{comms.lastCallDirection === "inbound" ? "📲" : "📱"}</span>
          <span className="font-medium text-slate-600">Last call</span>
          <span className="text-slate-400">{ago(comms.lastCallAt!)}</span>
          {comms.lastCallDuration != null && (
            <span className="text-slate-400">
              ({comms.lastCallDuration < 60
                ? `${comms.lastCallDuration}s`
                : `${Math.floor(comms.lastCallDuration / 60)}m ${comms.lastCallDuration % 60}s`})
            </span>
          )}
        </span>
      )}
    </div>
  );
}
